import React, { useState, useEffect } from 'react';
import { Modal, Button, Card, Badge } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { database } from '../firebase';
import { ref, onValue, set } from 'firebase/database';

interface EasterEggStats {
  timesActivated: number;
  lastActivated: number;
  secretMessage: string;
}

const KonamiEasterEgg: React.FC<{ show: boolean; onHide: () => void }> = ({ show, onHide }) => {
  const { currentUser } = useAuth();
  const [stats, setStats] = useState<EasterEggStats>({
    timesActivated: 0,
    lastActivated: 0,
    secretMessage: 'You found the secret! 🎮'
  });
  const [confetti, setConfetti] = useState<React.ReactNode[]>([]);

  useEffect(() => {
    if (!show) return;

    // Load easter egg stats from Firebase (globally, not tied to user)
    const statsRef = ref(database, `easter_eggs/konami_code`);
    const unsubscribe = onValue(statsRef, (snapshot) => {
      const data = snapshot.val() || {
        timesActivated: 0,
        lastActivated: 0,
        secretMessage: 'You found the secret! 🎮'
      };
      setStats(data);
    });

    // Create confetti effect
    createConfetti();

    return () => {
      unsubscribe();
    };
  }, [show]);
  
  // Create a ref outside the effect to track if we've updated stats
  const hasUpdatedStats = React.useRef(false);
  
  // Separate effect for updating stats to avoid infinite loop
  useEffect(() => {
    if (!show) return;
    
    // Only run once when the modal is shown
    const updateStats = async () => {
      try {
        const statsRef = ref(database, `easter_eggs/konami_code`);
        // Get the current stats directly from Firebase to avoid stale state
        const snapshot = await new Promise<any>(resolve => {
          onValue(statsRef, resolve, { onlyOnce: true });
        });
        
        const currentStats = snapshot.val() || {
          timesActivated: 0,
          lastActivated: 0,
          secretMessage: 'You found the secret! 🎮'
        };
        
        // Update with the latest values
        await set(statsRef, {
          timesActivated: currentStats.timesActivated + 1,
          lastActivated: Date.now(),
          secretMessage: currentStats.secretMessage
        });
      } catch (error) {
        console.error('Error updating easter egg stats:', error);
      }
    };

    // Use the ref to ensure this only runs once per modal show
    if (!hasUpdatedStats.current && show) {
      updateStats();
      hasUpdatedStats.current = true;
    }
    
    return () => {
      // Reset the flag when the component unmounts or modal closes
      if (!show) {
        hasUpdatedStats.current = false;
      }
    };
  }, [show]);

  const createConfetti = () => {
    const colors = ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff'];
    const pieces: React.ReactNode[] = [];

    for (let i = 0; i < 50; i++) {
      const left = Math.random() * 100;
      const top = Math.random() * 100;
      const size = Math.random() * 10 + 5;
      const color = colors[Math.floor(Math.random() * colors.length)];
      const animationDuration = Math.random() * 3 + 2;

      pieces.push(
        <div
          key={i}
          style={{
            position: 'absolute',
            left: `${left}%`,
            top: `${top}%`,
            width: `${size}px`,
            height: `${size}px`,
            backgroundColor: color,
            borderRadius: '50%',
            animation: `fall ${animationDuration}s linear forwards`,
            opacity: 0
          }}
        />
      );
    }

    setConfetti(pieces);
  };

  const formatDate = (timestamp: number) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleString();
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" backdrop="static">
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          pointerEvents: 'none'
        }}
      >
        {confetti}
      </div>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center">
          <span className="me-2" role="img" aria-label="trophy">🏆</span>
          <span style={{ background: 'linear-gradient(45deg, #ff0000, #ff7700, #ffff00, #00ff00, #0000ff, #8b00ff)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Konami Code Activated!!
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="text-center mb-4">
          <h2>⬆️ ⬆️ ⬇️ ⬇️ ⬅️ ➡️ ⬅️ ➡️ 🅱️ 🅰️</h2>
          <p className="lead mt-3">{stats.secretMessage}</p>
        </div>

        <Card className="mb-3">
          <Card.Body>
            <Card.Title>Developer Mode Unlocked</Card.Title>
            <div className="d-flex justify-content-between my-3">
              <div>
                <Badge bg="info" className="me-2">Version 1.0.0</Badge>
                <Badge bg="secondary">Build 20250420</Badge>
              </div>
              <div>
                <Badge bg="success">Moscht v4</Badge>
              </div>
            </div>
            <p>
              This app was built with React, Firebase, and ❤️ by a dedicated developer.
              Thanks for discovering this easter egg!
            </p>
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <Card.Title>Easter Egg Stats</Card.Title>
            <div className="mt-3">
              <div className="d-flex justify-content-between mb-2">
                <span>Times Activated:</span>
                <span>{stats.timesActivated}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Last Activated:</span>
                <span>{formatDate(stats.lastActivated)}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Secret Level:</span>
                <span>Master Gamer 🎮</span>
              </div>
            </div>
          </Card.Body>
        </Card>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button 
          variant="primary" 
          onClick={() => {
            // You could add another secret action here
            onHide();
          }}
        >
          Awesome!
        </Button>
      </Modal.Footer>
      <style>
        {`
          @keyframes fall {
            0% {
              transform: translateY(-10px) rotate(0deg);
              opacity: 1;
            }
            100% {
              transform: translateY(100vh) rotate(360deg);
              opacity: 0;
            }
          }
        `}
      </style>
    </Modal>
  );
};

export default KonamiEasterEgg;
